import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(...registerables);

const GenderStatsChart = ({ male = 45, female = 55, malePercentage, femalePercentage }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Calculate percentages if not provided
    const total = male + female;
    const malePercent = malePercentage
        ? parseFloat(malePercentage)
        : total > 0 ? Math.round((male / total) * 100 * 10) / 10 : 0;
    const femalePercent = femalePercentage
        ? parseFloat(femalePercentage)
        : total > 0 ? Math.round((female / total) * 100 * 10) / 10 : 0;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Reduced line lengths to save space
            // Increased line lengths for better separation
            const lineExtension = isMobile ? 12 : isTablet ? 18 : 25;
            const lineEndExtension = isMobile ? 12 : isTablet ? 18 : 25;

            // Significantly increased horizontal padding to prevent clipping of labels
            const xPadding = isMobile ? 80 : isTablet ? 100 : 130;
            const yPadding = isMobile ? 25 : isTablet ? 35 : 45;

            const labelFontSize = isMobile ? 14 : isTablet ? 16 : 18;
            const dotRadius = isMobile ? 3 : 4;
            const labelOffset = isMobile ? 6 : isTablet ? 8 : 10;

            const ctx = chartRef.current.getContext('2d');

            const calloutPlugin = {
                id: 'calloutPlugin',
                afterDraw(chart) {
                    const { ctx, data } = chart;
                    const percentages = [femalePercent, malePercent];

                    chart.getDatasetMeta(0).data.forEach((datapoint, index) => {
                        const { x, y, outerRadius, startAngle, endAngle } = datapoint;
                        const midAngle = (startAngle + endAngle) / 2;
                        const accentColor = index === 0 ? '#ff448c' : '#0576d3';

                        const dotX = x + Math.cos(midAngle) * outerRadius;
                        const dotY = y + Math.sin(midAngle) * outerRadius;

                        ctx.beginPath();
                        ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
                        ctx.fillStyle = accentColor;
                        ctx.fill();

                        const lineStartX = x + Math.cos(midAngle) * outerRadius;
                        const lineStartY = y + Math.sin(midAngle) * outerRadius;
                        const lineMidX = x + Math.cos(midAngle) * (outerRadius + lineExtension);
                        const lineMidY = y + Math.sin(midAngle) * (outerRadius + lineExtension);

                        const isRight = Math.cos(midAngle) > 0;
                        const lineEndX = lineMidX + (isRight ? lineEndExtension : -lineEndExtension);
                        const lineEndY = lineMidY;

                        ctx.beginPath();
                        ctx.moveTo(lineStartX, lineStartY);
                        ctx.lineTo(lineMidX, lineMidY);
                        ctx.lineTo(lineEndX, lineEndY);
                        ctx.strokeStyle = accentColor;
                        ctx.lineWidth = isMobile ? 1 : 1.5;
                        ctx.stroke();

                        const label = percentages[index] + '%';
                        ctx.font = `600 ${labelFontSize}px Roboto`;
                        ctx.fillStyle = accentColor;
                        ctx.textAlign = isRight ? 'left' : 'right';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(label, lineEndX + (isRight ? labelOffset : -labelOffset), lineEndY);
                    });
                }
            };

            chartInstance.current = new ChartJS(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Female', 'Male'],
                    datasets: [
                        {
                            data: [female, male],
                            backgroundColor: ['#ffb3cf', '#8ccbff'],
                            hoverBackgroundColor: ['#ff448c', '#0576d3'],
                            borderWidth: 0,
                            cutout: isMobile ? '70%' : '65%',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            left: xPadding,
                            right: xPadding,
                            top: yPadding,
                            bottom: yPadding
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            titleFont: { size: isMobile ? 11 : 12 },
                            bodyFont: { size: isMobile ? 11 : 12 },
                            padding: isMobile ? 8 : 10,
                            callbacks: {
                                label: (context) => {
                                    const label = context.label || '';
                                    const value = context.raw || 0;
                                    const percentage = context.dataIndex === 0 ? femalePercent : malePercent;
                                    return `${label}: ${value} (${percentage}%)`;
                                }
                            }
                        },
                    },
                },
                plugins: [calloutPlugin],
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, male, female, malePercent, femalePercent]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default GenderStatsChart;
